import { useState, useMemo } from 'react';
import { CheckCircle2, AlertTriangle, Send, ChevronRight, X, Loader2 } from 'lucide-react';

type Mode = 'send' | 'approval';

interface ChecklistItem {
  id: string;
  label: string;
  hint?: string;
  /** Wenn gesetzt: vom System schon erfüllt — Haken ist gesetzt + nicht abwählbar */
  autoOk?: boolean;
  /** Wenn gesetzt: Warnt rot wenn nicht erfüllt */
  autoWarn?: { ok: boolean; message: string };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  pending: boolean;
  mode: Mode;
  /** Beschriftung des Knopfs (Standard: „Jetzt versenden" bzw. „Zur Freigabe einreichen") */
  ctaLabel?: string;
  /** Daten für automatische Checks */
  meta: {
    name?: string;
    subject?: string;
    senderName?: string;
    senderEmail?: string;
    recipientGroupCount: number;
    manualRecipientCount: number;
    hasUnsubscribe: boolean;
    hasBrowserView: boolean;
    designMode: 'html' | 'template' | null | undefined;
    hasSentTestEmail: boolean;
    /** Übrig gebliebene Platzhalter wie [DATUM] (aus einer HTML-Vorlage) */
    placeholders?: string[];
  };
}

export function PreSendChecklist({ isOpen, onClose, onConfirm, pending, mode, meta, ctaLabel: knopf }: Props) {
  // System-Items sind in items implizit. Manuelle Items hier:
  const manualItems: ChecklistItem[] = useMemo(() => [
    {
      id: 'recipients_reviewed',
      label: 'Empfänger sind richtig',
      hint: `${meta.recipientGroupCount} Gruppe${meta.recipientGroupCount === 1 ? '' : 'n'} + ${meta.manualRecipientCount} einzelne Empfänger — wirklich diese Personen sollen die Mail bekommen?`,
    },
    {
      id: 'subject_checked',
      label: 'Betreff ist korrekt geschrieben',
      hint: `„${meta.subject ?? '—'}" — keine Tippfehler? Maximal 50 Zeichen empfohlen.`,
    },
    {
      id: 'spelling',
      label: 'Rechtschreibung & Tonalität geprüft',
      hint: 'Idealerweise von einer zweiten Person gelesen. Vorsicht bei Eigennamen, Zahlen, Datumsangaben.',
    },
    {
      id: 'links',
      label: 'Alle Links und Bilder funktionieren',
      hint: 'Test-E-Mail an dich selbst geschickt? Im echten Postfach (nicht nur Vorschau) gecheckt? Bilder werden geladen?',
    },
    {
      id: 'sender',
      label: 'Absender ist korrekt',
      hint: `${meta.senderName ?? ''} <${meta.senderEmail ?? ''}> — gewollte Absender-Identität?`,
    },
    {
      id: 'undo_aware',
      label: mode === 'send'
        ? 'Mir ist klar, dass der Versand nicht rückgängig gemacht werden kann'
        : 'Mir ist klar, dass die Redaktion die Mail jetzt zur Freigabe sieht',
    },
  ], [meta, mode]);

  // Auto-Items (vom System geprüft / vorausgefüllt)
  const autoItems: ChecklistItem[] = useMemo(() => [
    {
      id: 'auto_member_protection',
      label: 'Mitglieder-Schutz aktiv: nur zahlungsaktive Mitglieder werden angeschrieben',
      hint: 'Ex-Mitglieder, Probeschüler und CRM-Kontakte ohne aktiven Vertrag bekommen automatisch opt_in=false und werden vom Versand ausgeschlossen. Wer aktuell blockiert ist, steht in den "[Audit]"-Gruppen unter Empfänger-Gruppen.',
      autoOk: true,
    },
    {
      id: 'auto_unsubscribe',
      label: 'Abmelde-Link wird eingefügt',
      hint: 'DSGVO-Pflicht. Brevo ergänzt {{ unsubscribe }} automatisch im Footer.',
      autoOk: meta.hasUnsubscribe,
      autoWarn: meta.hasUnsubscribe ? undefined : { ok: false, message: 'Abmelde-Link ist DEAKTIVIERT — bitte im Design-Step wieder aktivieren!' },
    },
    {
      id: 'auto_browser',
      label: 'Browser-Ansicht-Hinweis aktiv',
      hint: 'Optional, hilft Empfängern bei Darstellungsproblemen.',
      autoOk: meta.hasBrowserView,
    },
    {
      id: 'auto_design',
      label: 'Inhalt vorhanden',
      hint: meta.designMode ? 'Design-Modus gesetzt.' : 'Kein Inhalt!',
      autoOk: !!meta.designMode,
      autoWarn: meta.designMode ? undefined : { ok: false, message: 'Es gibt noch kein Design / kein HTML — bitte erst im Design-Step abschließen.' },
    },
    {
      id: 'auto_placeholders',
      label: 'Keine Platzhalter mehr in der Mail',
      hint: 'Texte in [ECKIGEN KLAMMERN] aus der Vorlage sind alle ersetzt.',
      autoOk: (meta.placeholders?.length ?? 0) === 0,
      autoWarn: (meta.placeholders?.length ?? 0) > 0
        ? {
            ok: false,
            message: `Noch ${meta.placeholders!.length === 1 ? 'ein Platzhalter' : `${meta.placeholders!.length} Platzhalter`} in der Mail: `
              + `${meta.placeholders!.slice(0, 5).join(', ')}${meta.placeholders!.length > 5 ? ' …' : ''} — bitte ersetzen.`,
          }
        : undefined,
    },
    {
      id: 'auto_recipients',
      label: 'Empfänger vorhanden',
      hint: `${meta.recipientGroupCount} Gruppe${meta.recipientGroupCount === 1 ? '' : 'n'} + ${meta.manualRecipientCount} einzelne Empfänger.`,
      autoOk: (meta.recipientGroupCount + meta.manualRecipientCount) > 0,
      autoWarn: (meta.recipientGroupCount + meta.manualRecipientCount) === 0
        ? { ok: false, message: 'Keine Empfänger — bitte im Empfänger-Step mindestens eine Gruppe wählen ODER einzelne Empfänger hinzufügen.' }
        : undefined,
    },
    {
      id: 'auto_test',
      label: 'Test-E-Mail wurde verschickt',
      hint: meta.hasSentTestEmail ? 'Test-Versand vor wenigen Sekunden/Minuten gemacht.' : 'Wir empfehlen vorher mindestens eine Test-Mail an dich selbst.',
      autoOk: meta.hasSentTestEmail,
      // Kein Warn — nur eine Empfehlung
    },
  ], [meta]);

  const [checked, setChecked] = useState<Record<string, boolean>>({});

  // Blocker: harte Auto-Warnungen (z.B. kein Inhalt, keine Empfänger, kein Abmelde-Link)
  const blockers = autoItems.filter(i => i.autoWarn && !i.autoWarn.ok);
  const allManualChecked = manualItems.every(i => checked[i.id]);
  const canSend = blockers.length === 0 && allManualChecked;

  if (!isOpen) return null;

  const ctaLabel = mode === 'send'
    ? (pending ? 'Wird an Brevo übergeben...' : knopf ?? 'Jetzt versenden')
    : (pending ? 'Wird eingereicht...' : knopf ?? 'Zur Freigabe einreichen');

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !pending) onClose(); }}
    >
      <div className="modal modal-lg" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <h2 className="modal-title">
            {mode === 'send' ? 'Letzter Check vor dem Versand' : 'Letzter Check vor der Freigabe-Einreichung'}
          </h2>
          <button className="modal-close" onClick={onClose} title="Schließen" disabled={pending}><X size={18} /></button>
        </div>
        <div className="modal-content">
          {pending && (
            <div
              className="alert alert-info"
              role="status"
              aria-live="polite"
              style={{ marginBottom: '1.25rem', alignItems: 'center' }}
            >
              <Loader2 size={18} className="spin" aria-hidden="true" />
              <div>
                <strong>
                  {mode === 'send'
                    ? 'Kampagne wird an Brevo übergeben...'
                    : 'Kampagne wird zur Freigabe eingereicht...'}
                </strong>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                  {mode === 'send'
                    ? 'Empfänger werden geprüft und an Brevo übertragen. Bitte das Fenster nicht schließen.'
                    : 'Bitte das Fenster nicht schließen — die Redaktion wird gleich benachrichtigt.'}
                </div>
              </div>
            </div>
          )}
          <p style={{ margin: '0 0 1.25rem', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            Bitte hak alle Punkte ab — nimm dir wirklich die paar Sekunden. Eine versendete E-Mail
            kann nicht zurückgeholt werden, und ein Tippfehler im Betreff geht an
            <strong> alle ausgewählten Empfänger</strong>.
          </p>

          {/* Blocker zeigen */}
          {blockers.length > 0 && (
            <div className="alert alert-danger" style={{ marginBottom: '1.25rem' }}>
              <AlertTriangle size={16} />
              <div>
                <strong>Versand blockiert:</strong>
                <ul style={{ margin: '0.4rem 0 0 1rem', padding: 0 }}>
                  {blockers.map(b => <li key={b.id}>{b.autoWarn!.message}</li>)}
                </ul>
              </div>
            </div>
          )}

          {/* Auto-Checks */}
          <div className="card" style={{ marginBottom: '1.25rem', background: 'var(--surface-secondary)' }}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Vom System geprüft</h3>
            {autoItems.map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', padding: '0.4rem 0' }}>
                <span style={{
                  width: 20, height: 20, flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%',
                  background: item.autoOk ? 'var(--success-light)' : 'var(--surface-secondary)',
                  color: item.autoOk ? 'var(--success-dark)' : 'var(--text-tertiary)',
                  border: '1px solid ' + (item.autoOk ? 'var(--success-dark)' : 'var(--border-color)'),
                  marginTop: 2,
                }}>
                  {item.autoOk ? <CheckCircle2 size={14} /> : '·'}
                </span>
                <div>
                  <div style={{ fontWeight: 500, color: item.autoOk ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                    {item.label}
                  </div>
                  {item.hint && <div style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)', marginTop: 2 }}>{item.hint}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Manuelle Checks */}
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Bitte selbst bestätigen</h3>
          {manualItems.map(item => (
            <label key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', padding: '0.5rem 0', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={!!checked[item.id]}
                onChange={(e) => setChecked({ ...checked, [item.id]: e.target.checked })}
                style={{ accentColor: 'var(--primary)', width: 18, height: 18, marginTop: 2, flexShrink: 0 }}
                disabled={pending}
              />
              <div>
                <div style={{ fontWeight: 500 }}>{item.label}</div>
                {item.hint && <div style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)', marginTop: 2 }}>{item.hint}</div>}
              </div>
            </label>
          ))}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.75rem', paddingTop: '1.25rem', borderTop: '1px solid var(--border-light)' }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={pending}>
              Zurück, nochmal prüfen
            </button>
            <button className="btn btn-primary" onClick={onConfirm} disabled={!canSend || pending}>
              {pending
                ? <Loader2 size={16} className="spin" aria-hidden="true" />
                : (mode === 'send' ? <Send size={16} /> : <ChevronRight size={16} />)}
              {ctaLabel}
            </button>
          </div>

          {!canSend && blockers.length === 0 && (
            <p style={{ marginTop: '0.75rem', textAlign: 'right', fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
              {Object.values(checked).filter(Boolean).length} von {manualItems.length} Punkten bestätigt
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
